import { useCallback, useRef } from 'react';

// Sci-fi / Tech UI sounds using Web Audio API

let audioContext: AudioContext | null = null;

const initAudioContext = (): AudioContext | null => {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (e) {
      return null;
    }
  }
  return audioContext;
};

// Sci-fi hover sound - quick digital blip with frequency sweep
const playHoverSound = async () => {
  const ctx = initAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    await ctx.resume();
  }

  const now = ctx.currentTime;

  // Main tone with upward sweep
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.setValueAtTime(800, now);
  osc.frequency.exponentialRampToValueAtTime(1600, now + 0.05);

  gain.gain.setValueAtTime(0.25, now);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.08);

  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.start(now);
  osc.stop(now + 0.1);

  // Add subtle high frequency shimmer
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();

  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(2400, now);

  gain2.gain.setValueAtTime(0.08, now);
  gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.04);

  osc2.connect(gain2);
  gain2.connect(ctx.destination);

  osc2.start(now);
  osc2.stop(now + 0.05);
};

// Sci-fi click sound - layered digital confirmation tone
const playClickSound = async () => {
  const ctx = initAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    await ctx.resume();
  }

  const now = ctx.currentTime;

  // Base tone - quick downward sweep
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();

  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(1200, now);
  osc1.frequency.exponentialRampToValueAtTime(400, now + 0.08);

  gain1.gain.setValueAtTime(0.35, now);
  gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.12);

  osc1.connect(gain1);
  gain1.connect(ctx.destination);

  osc1.start(now);
  osc1.stop(now + 0.15);

  // Mid harmonic
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();

  osc2.type = 'triangle';
  osc2.frequency.setValueAtTime(600, now);
  osc2.frequency.exponentialRampToValueAtTime(300, now + 0.1);

  gain2.gain.setValueAtTime(0.2, now);
  gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.1);

  osc2.connect(gain2);
  gain2.connect(ctx.destination);

  osc2.start(now);
  osc2.stop(now + 0.12);

  // High accent - quick blip
  const osc3 = ctx.createOscillator();
  const gain3 = ctx.createGain();

  osc3.type = 'sine';
  osc3.frequency.setValueAtTime(1800, now + 0.02);

  gain3.gain.setValueAtTime(0, now);
  gain3.gain.setValueAtTime(0.15, now + 0.02);
  gain3.gain.exponentialRampToValueAtTime(0.01, now + 0.06);

  osc3.connect(gain3);
  gain3.connect(ctx.destination);

  osc3.start(now);
  osc3.stop(now + 0.08);
};

export const useSound = () => {
  const lastHoverTime = useRef(0);
  const HOVER_DEBOUNCE = 80;

  const onHover = useCallback(() => {
    const now = Date.now();
    if (now - lastHoverTime.current > HOVER_DEBOUNCE) {
      lastHoverTime.current = now;
      playHoverSound();
    }
  }, []);

  const onClick = useCallback(() => {
    playClickSound();
  }, []);

  return { onHover, onClick };
};

export default useSound;
